// Copyright © 2026 Alex Bruce. All rights reserved.
//
// The org's shared JSON libraries: presets, labour rates, exclusions, the
// logo, the submittal template layer.
//
// Each of these lived in a browser, so a firm of five had five private copies
// of what were meant to be its standards. They are documents edited whole
// rather than row-by-row collections, which is why they share one key/value
// table while lessons and phases got real ones.
//
// Saving carries the timestamp the value was loaded at. If the row has moved
// on since, the write is REFUSED and the caller re-reads. Last-write-wins is
// fine for a preference and wrong for a firm's labour rates.

import { supabase } from "./supabase";

export type OrgSettingKey =
  | "presets"
  | "labor_rates"
  | "org_exclusions"
  | "org_logo"
  | "submittal_templates";

export interface OrgSetting<T> {
  value: T;
  /** Pass back to saveOrgSetting to detect a concurrent change. */
  updatedAt: string | null;
}

/** Null value means the org has never saved this library. */
export async function loadOrgSetting<T>(
  orgId: string, key: OrgSettingKey,
): Promise<OrgSetting<T | null>> {
  if (!supabase) return { value: null, updatedAt: null };
  const { data, error } = await supabase
    .from("org_settings")
    .select("value,updated_at")
    .eq("org_id", orgId).eq("key", key)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { value: null, updatedAt: null };
  return { value: (data.value ?? null) as T | null, updatedAt: data.updated_at ?? null };
}

/**
 * The result of a write. Flat rather than a discriminated union: the union
 * read better but would not narrow through the hook's closure, and fighting
 * that for cosmetics is not worth a reader's confusion.
 *
 * `conflict` means a colleague saved first and `theirs` is what is stored now;
 * nothing was written.
 */
export interface SaveOutcome<T> {
  ok: boolean;
  updatedAt?: string | null;
  conflict?: boolean;
  theirs?: T | null;
  error?: string;
}

/**
 * Write one library.
 *
 * `expectedUpdatedAt` is the timestamp this value was loaded at, or null when
 * the caller believes no row exists yet. A mismatch means a colleague saved in
 * between; nothing is written and their version comes back so the caller can
 * decide, rather than a quiet overwrite of a change nobody saw.
 */
export async function saveOrgSetting<T>(
  orgId: string, key: OrgSettingKey, value: T,
  expectedUpdatedAt: string | null, userId: string,
): Promise<SaveOutcome<T>> {
  if (!supabase) return { ok: false, conflict: false, error: "Not connected" };

  let current: OrgSetting<T | null>;
  try {
    current = await loadOrgSetting<T>(orgId, key);
  } catch (e: any) {
    return { ok: false, conflict: false, error: e.message };
  }
  if ((current.updatedAt ?? null) !== (expectedUpdatedAt ?? null)) {
    return { ok: false, conflict: true, theirs: current.value, updatedAt: current.updatedAt };
  }

  const now = new Date().toISOString();
  const { error } = await supabase.from("org_settings").upsert(
    { org_id: orgId, key, value: value as any, updated_at: now, updated_by: userId },
    { onConflict: "org_id,key" },
  );
  if (error) {
    // The write policy is owner/admin, so this is where a plain member lands.
    const friendly = /row-level security/i.test(error.message)
      ? "Only an organisation owner or admin can change shared libraries."
      : error.message;
    return { ok: false, conflict: false, error: friendly };
  }
  return { ok: true, updatedAt: now };
}
